import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Check, X } from 'lucide-react';
import { PLANS, formatProductLimit, isCapTier, type PlanInfo } from '@/lib/plans';
import { includedMcAllowance } from '@/lib/mcLimitMarker';

type FlagKey = keyof PlanInfo['featureFlags'];

/** Minimal shape of the `t` function from `useTranslation()`. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

// `dedicatedManager` bleibt draussen: kein oeffentliches Band traegt das Flag.
const FEATURE_KEYS: { key: FlagKey; labelKey: string }[] = [
  { key: 'aiTitleDescription', labelKey: 'aiTitleDescription' },
  { key: 'mcConnect', labelKey: 'merchantCenterConnect' },
  { key: 'feedHosting', labelKey: 'feedHosting' },
  { key: 'customPrompts', labelKey: 'customPrompts' },
  { key: 'multiMC', labelKey: 'multipleMerchantCenters' },
  { key: 'multiLang', labelKey: 'multiLanguage' },
  { key: 'prioritySupport', labelKey: 'prioritySupport' },
];

export interface ComparisonGroup {
  key: string;
  plans: PlanInfo[];
}

/**
 * Aufeinanderfolgende Baender mit identischen Funktionen werden eine Spalte
 * (UX-Audit A7O-10: elf Spalten, die letzte angeschnitten, mobil zwei sichtbar).
 * Heute: Free, 500 bis 2.000, 5.000 bis 25.000, 50.000 bis 100.000.
 */
export function comparisonGroups(plans: PlanInfo[] = PLANS): ComparisonGroup[] {
  const groups: ComparisonGroup[] = [];
  for (const plan of plans) {
    const last = groups[groups.length - 1];
    const joinable =
      !!last &&
      isCapTier(plan.tier) &&
      isCapTier(last.plans[0].tier) &&
      FEATURE_KEYS.every(({ key }) => last.plans[0].featureFlags[key] === plan.featureFlags[key]);
    if (joinable) last.plans.push(plan);
    else groups.push({ key: plan.tier, plans: [plan] });
  }
  return groups;
}

/**
 * Der Wert (1 / 3 / unbegrenzt) fuer ein einzelnes Band, aus derselben Quelle
 * wie das Add-on-Gate, nie neu erfunden.
 */
function allowanceLabel(value: number, t: Translate): string {
  return Number.isFinite(value) ? String(value) : t('comparison.allowanceUnlimited');
}

/**
 * Ruling P9: `comparisonGroups` fasst cap5000/cap10000/cap25000 zu einer Stufe
 * zusammen, weil ihre BOOLESCHEN Flags (`multiMC`/`multiLang`) alle drei
 * gleich sind, die tatsaechlich inkludierte Menge ist es aber nicht (3 gegen
 * unbegrenzt ab 25.000). Eine Stufe, die das verschweigt, ist eine falsche
 * Zusammenfassung. Diese Zeile zeigt deshalb den ECHTEN Wert je Stufe: einen
 * einzelnen, wenn er innerhalb der Stufe gleich bleibt, sonst den Bereich vom
 * ersten bis zum letzten Band der Stufe (`includedMcAllowance`, Spiegel von
 * `supabase/functions/_shared/plan-features.ts:includedAllowance`).
 *
 * Fix-Runde 1 (Review-Befund): der Bereich allein ("3 bis unbegrenzt") sagt
 * zwar, DASS sich die Menge innerhalb der Stufe aendert, aber nicht WO, ein
 * Kaeufer von cap25000 sah nirgends, dass GENAU SEIN Band unbegrenzt bekommt.
 * Die Schwellenbaender werden deshalb mitgenannt: das letzte Band, das noch
 * die Anfangsmenge traegt (`fromLimit`), und das erste Band mit der
 * abweichenden Menge (`toLimit`), abgeleitet aus `group.plans`, nicht
 * hartkodiert.
 */
function mcAndLanguagesLabel(group: ComparisonGroup, language: string | undefined, t: Translate): string {
  const plans = group.plans;
  const first = includedMcAllowance(plans[0].productLimit);
  const last = includedMcAllowance(plans[plans.length - 1].productLimit);
  if (first === last) return allowanceLabel(first, t);

  let fromPlan = plans[0];
  let toPlan = plans[plans.length - 1];
  for (const plan of plans) {
    if (includedMcAllowance(plan.productLimit) === first) fromPlan = plan;
    else {
      toPlan = plan;
      break;
    }
  }

  return t('comparison.allowanceRange', {
    from: allowanceLabel(first, t),
    to: allowanceLabel(last, t),
    fromLimit: formatProductLimit(fromPlan, language, t),
    toLimit: formatProductLimit(toPlan, language, t),
  });
}

interface FeatureComparisonTableProps {
  currentTier: string;
}

export const FeatureComparisonTable: React.FC<FeatureComparisonTableProps> = ({ currentTier }) => {
  const { t, i18n } = useTranslation('pricing');
  const language = i18n?.language;
  const groups = comparisonGroups();

  const groupLabel = (group: ComparisonGroup) => {
    const first = group.plans[0];
    const last = group.plans[group.plans.length - 1];
    if (!isCapTier(first.tier)) return t('comparison.freeHeader', { limit: formatProductLimit(first, language, t) });
    return t('comparison.bandRange', {
      from: formatProductLimit(first, language, t),
      to: formatProductLimit(last, language, t),
    });
  };
  const isCurrentGroup = (group: ComparisonGroup) => group.plans.some((p) => p.tier === currentTier);

  const mark = (included: boolean) => (
    <>
      {included ? (
        <Check className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/40 shrink-0" aria-hidden="true" />
      )}
      <span className="sr-only">{included ? t('comparison.included') : t('comparison.notIncluded')}</span>
    </>
  );

  return (
    <>
      <ul className="space-y-3 sm:hidden" data-testid="comparison-mobile">
        {groups.map((group) => (
          <li
            key={group.key}
            className={`rounded-lg border p-4 ${isCurrentGroup(group) ? 'border-primary/50 bg-primary/5' : ''}`}
          >
            <p className="mb-2 text-sm font-semibold">{groupLabel(group)}</p>
            <ul className="space-y-1.5">
              {FEATURE_KEYS.map(({ key, labelKey }) => (
                <li key={key} className="flex items-center gap-2 text-sm">
                  {mark(group.plans[0].featureFlags[key])}
                  <span>{t(`comparison.${labelKey}`)}</span>
                </li>
              ))}
              <li className="flex items-center justify-between gap-2 border-t pt-1.5 text-sm">
                <span>{t('comparison.mcAndLanguagesIncluded')}</span>
                <span className="font-medium">{mcAndLanguagesLabel(group, language, t)}</span>
              </li>
            </ul>
          </li>
        ))}
      </ul>

      <div className="hidden sm:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px]">{t('comparison.feature')}</TableHead>
              {groups.map((group) => (
                <TableHead
                  key={group.key}
                  className={`text-center ${isCurrentGroup(group) ? 'bg-primary/5' : ''}`}
                >
                  {groupLabel(group)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {FEATURE_KEYS.map(({ key, labelKey }) => (
              <TableRow key={key}>
                <TableCell className="font-medium text-sm">{t(`comparison.${labelKey}`)}</TableCell>
                {groups.map((group) => (
                  <TableCell
                    key={group.key}
                    className={`text-center ${isCurrentGroup(group) ? 'bg-primary/5' : ''}`}
                  >
                    <span className="inline-flex justify-center">{mark(group.plans[0].featureFlags[key])}</span>
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="font-medium text-sm">{t('comparison.mcAndLanguagesIncluded')}</TableCell>
              {groups.map((group) => (
                <TableCell
                  key={group.key}
                  className={`text-center text-sm ${isCurrentGroup(group) ? 'bg-primary/5' : ''}`}
                >
                  {mcAndLanguagesLabel(group, language, t)}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </>
  );
};
